import { debounce as lodashDebounce } from "lodash";
import { type Question, type SurveyModel } from "survey-core";
import { apiRequestContractHandler } from "shared/api/apiRequestContractHandler";
import { getSocietyIdentityContract } from "shared/api/contracts/society/societyId/identity";
import type { LoginDetailsFormValues } from "shared/components/survey-js-form/questions/login-details/LoginDetailsQuestion";
import { type ContactInformationFormData } from "shared/components/survey-js-form/questions/profile-address-contact-information/IProfileAddressContactInformationQuestion";
import { filterNonEmptyAddressContactInfo } from "shared/components/survey-js-form/questions/profile-address-contact-information/registerProfileAddressContactInformationQuestion";

export const getPasswordConfirmVerificationState = (
  password: string | undefined,
  confirmPassword: string,
) => {
  if (!confirmPassword) {
    return {
      error: "",
      hasError: false,
    };
  }
  if (password !== confirmPassword) {
    return {
      error: "Passwords do not match.",
      hasError: true,
    };
  }
  return {
    error: "",
    hasError: false,
  };
};

export const getPasswordVerificationState = (password: string | undefined) => {
  if (!password) {
    return {
      errors: [],
      hasErrors: false,
      confirmPasswordReadOnly: true,
      clearConfirmPassword: true,
    };
  }

  const errors = [
    password.length < 8
      ? "Your password must be at least 8 characters long."
      : null,
    !/(?=.*[A-Z])/.test(password)
      ? "Your password must contain an uppercase letter."
      : null,
    !/(?=.*[a-z])/.test(password)
      ? "Your password must contain a lowercase letter."
      : null,
    !/(?=.*\d)/.test(password) ? "Your password must contain a number." : null,
    !/[!@#$%^&*(),.?":{}|<>_]/.test(password)
      ? "Your password must contain a symbol."
      : null,
  ].filter(Boolean) as string[];

  return {
    errors,
    hasErrors: errors.length > 0,
    confirmPasswordReadOnly: errors.length > 0,
    clearConfirmPassword: errors.length > 0,
  };
};

export const getLoginDetailsValidationState = (
  values: LoginDetailsFormValues | undefined,
  {
    passwordRequired = true,
    confirmPasswordRequired = true,
    oldPasswordRequired = false,
  }: {
    passwordRequired?: boolean;
    confirmPasswordRequired?: boolean;
    oldPasswordRequired?: boolean;
  } = {},
) => {
  const user_name = values?.user_name ?? "";
  const password = values?.password ?? "";
  const password_confirm = values?.password_confirm ?? "";
  const old_password = values?.old_password ?? "";

  const requiredErrors: string[] = [];
  const isChangingPassword = !!password || !!password_confirm;

  if (!user_name) {
    requiredErrors.push("Email is required.");
  }

  if (passwordRequired || isChangingPassword) {
    if (!password) {
      requiredErrors.push("Password is required.");
    }
  }

  if (confirmPasswordRequired || isChangingPassword) {
    if (!password_confirm) {
      requiredErrors.push("Confirm Password is required.");
    }
  }

  if (oldPasswordRequired && isChangingPassword && !old_password) {
    requiredErrors.push("Old Password is required.");
  }

  const passwordState = getPasswordVerificationState(password);

  const normalizedPasswordConfirm = passwordState.clearConfirmPassword
    ? ""
    : password_confirm;

  const shouldValidatePasswordRules =
    passwordRequired || confirmPasswordRequired || isChangingPassword;

  const confirmPasswordState =
    shouldValidatePasswordRules &&
    !passwordState.confirmPasswordReadOnly &&
    normalizedPasswordConfirm
      ? getPasswordConfirmVerificationState(password, normalizedPasswordConfirm)
      : { error: "", hasError: false };

  const validationErrors = shouldValidatePasswordRules
    ? [
        ...passwordState.errors,
        ...(confirmPasswordState.error ? [confirmPasswordState.error] : []),
      ]
    : [];

  return {
    normalizedValues: {
      ...values,
      user_name,
      password,
      password_confirm: normalizedPasswordConfirm,
      ...(oldPasswordRequired ? { old_password } : {}),
    },
    requiredErrors,
    validationErrors,
    confirmPasswordReadOnly: shouldValidatePasswordRules
      ? passwordState.confirmPasswordReadOnly
      : true,
    clearConfirmPassword: passwordState.clearConfirmPassword,
    hasErrors: requiredErrors.length > 0 || validationErrors.length > 0,
  };
};

export const createValidateLoginDetailsQuestion =
  ({
    question,
    passwordRequired = true,
    confirmPasswordRequired = true,
    oldPasswordRequired = false,
  }: {
    question: Question;
    passwordRequired?: boolean;
    confirmPasswordRequired?: boolean;
    oldPasswordRequired?: boolean;
  }) =>
  (
    sender: SurveyModel,
    options: {
      question: Question;
      value: LoginDetailsFormValues;
      error?: string;
    },
  ) => {
    if (options.question !== question) {
      return;
    }

    const validationState = getLoginDetailsValidationState(options.value, {
      passwordRequired,
      confirmPasswordRequired,
      oldPasswordRequired,
    });

    if (validationState.requiredErrors.length > 0) {
      options.error = validationState.requiredErrors.join("\n");
      return;
    }

    if (validationState.validationErrors.length > 0) {
      options.error = validationState.validationErrors.join("\n");
    }
  };

export const debouncedCheckUsername = lodashDebounce(
  async (
    societyId: number,
    value: string,
    setIsUnique: (isUnique: boolean) => void,
    setLookupError: (lookupError: string | undefined) => void,
  ) => {
    try {
      const result = await apiRequestContractHandler(
        getSocietyIdentityContract,
        {
          params: {
            username: value,
            societyId,
          },
        },
      );
      setLookupError(undefined);
      setIsUnique(!result.exists);
    } catch (e) {
      // The API reports a missing identity provider or a failed Cognito
      // lookup as a PillarError, which arrives here as the message to show.
      // It always starts with the public half - "Account creation currently
      // unavailable." - and outside prod, or for an admin session, carries the
      // privileged detail appended after a "(reason)" tag.
      console.error(e);
      setLookupError(
        e instanceof Error
          ? e.message
          : "Unable to verify this e-mail address right now.",
      );
      // Uniqueness is unknown rather than failed, so leave the conflict
      // message alone - the lookup error is what needs showing.
      setIsUnique(true);
    }
  },
  1000,
);

export const customErrorMessage = ({
  question,
  userHubUrl,
}: {
  question: Question;
  userHubUrl?: string;
}) => {
  const customConflictErrorMessage = question.customConflictErrorMessage;

  if (customConflictErrorMessage) {
    return (
      <div
        data-testid="email-conflict-error"
        dangerouslySetInnerHTML={{ __html: customConflictErrorMessage }}
      />
    );
  }

  return (
    <p
      className="text-status-danger-tint text-xs font-bold"
      data-testid="email-conflict-error"
    >
      Sorry that e-mail is unavailable{" "}
      <a
        href={`${userHubUrl}/account/recover`}
        className="text-primary-600 hover:text-primary-800 underline"
      >
        Click here!
      </a>{" "}
      to attempt recovering an existing account.
    </p>
  );
};

export const updateAdditionalProfileContactInfoInForm = ({
  question,
  values,
}: {
  question: Question;
  values: LoginDetailsFormValues;
}) => {
  const allProfileAddressContactInformationQuestions = (
    question.survey as SurveyModel
  )
    .getAllQuestions()
    .filter((q) => q.getType() === "profileaddresscontactinformation");

  for (const profileAddressContactInformationQuestion of allProfileAddressContactInformationQuestions) {
    const existingContactValue =
      profileAddressContactInformationQuestion.value ?? {};

    profileAddressContactInformationQuestion.updateValueFromSurvey({
      ...existingContactValue,
      email: values.user_name,
    });

    const surveyModel =
      profileAddressContactInformationQuestion.getSurvey() as SurveyModel;
    const surveyData = surveyModel.getSurveyData();

    const contactInformationData: ContactInformationFormData[] =
      surveyData.getValue("contactInformation") ?? [];

    let singleQuestionContactInformationData = contactInformationData.find(
      (data) =>
        data.question_name === profileAddressContactInformationQuestion.name,
    );

    if (!singleQuestionContactInformationData) {
      singleQuestionContactInformationData = {
        question_name: profileAddressContactInformationQuestion.name,
      };
      contactInformationData.push(singleQuestionContactInformationData);
    }

    const contactQuestionValues =
      profileAddressContactInformationQuestion.contentPanel.getValue();

    singleQuestionContactInformationData.email = values.user_name;
    singleQuestionContactInformationData.phone = contactQuestionValues.phone;
    singleQuestionContactInformationData.fax = contactQuestionValues.fax;
    singleQuestionContactInformationData.use = contactQuestionValues.use_id;
    singleQuestionContactInformationData.type = contactQuestionValues.type_id;

    if (contactQuestionValues.contact_information_id) {
      singleQuestionContactInformationData.id =
        contactQuestionValues.contact_information_id;
    }

    surveyData.setValue(
      "contactInformation",
      filterNonEmptyAddressContactInfo(contactInformationData),
      false,
      false,
    );
  }
};
